import { useState, useCallback, useEffect } from 'react';
import { myProfileApi } from '../api/myProfileApi';
import { appToast } from '../../../../components/AppToast';

export function useMyProfile() {
  const [profile, setProfile] = useState(null);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [activeModal, setActiveModal] = useState(null); // 'family', 'qualification', 'skills', etc.
  const [modalFormData, setModalFormData] = useState({});

  const loadProfile = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await myProfileApi.getProfile();
      if (res?.data) {
        setProfile(res.data);
        setFormData(res.data.personal || {});
      }
    } catch {
      appToast.error('Failed to load profile details');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          avatarUrl: reader.result,
        }));
        appToast.success('Profile photo uploaded. Click Save to apply.');
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = async (e) => {
    if (e) e.preventDefault();
    setIsSaving(true);
    try {
      const updated = {
        ...profile,
        personal: { ...formData },
      };
      await myProfileApi.updateProfile(updated);
      setProfile(updated);
      appToast.success('Profile information saved successfully');
    } catch {
      appToast.error('Failed to save profile information');
    } finally {
      setIsSaving(false);
    }
  };

  // Add items to sub-sections
  const handleOpenAddModal = (section) => {
    setActiveModal(section);
    setModalFormData({});
  };

  const handleCloseModal = () => {
    setActiveModal(null);
    setModalFormData({});
  };

  const handleModalFieldChange = (field, value) => {
    setModalFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveModalItem = () => {
    if (!activeModal) return;
    const newItem = {
      id: Date.now(),
      ...modalFormData,
    };
    setProfile((prev) => ({
      ...prev,
      [activeModal]: [...(prev[activeModal] || []), newItem],
    }));
    appToast.success(`Added entry to ${activeModal}`);
    handleCloseModal();
  };

  const handleDeleteItem = (section, id) => {
    setProfile((prev) => ({
      ...prev,
      [section]: (prev[section] || []).filter((item) => item.id !== id),
    }));
    appToast.success('Entry removed');
  };

  return {
    state: {
      profile,
      formData,
      official: profile?.official || {},
      isLoading,
      isSaving,
      activeTab,
      activeModal,
      modalFormData,
    },
    actions: {
      setActiveTab,
      handleFieldChange,
      handlePhotoUpload,
      handleSaveProfile,
      handleOpenAddModal,
      handleCloseModal,
      handleModalFieldChange,
      handleSaveModalItem,
      handleDeleteItem,
      reloadProfile: loadProfile,
    },
  };
}
